<script setup lang="ts">
/**
 * Tilgangs-notifikationer ([D-218], Charlie 19-08-2026) — KUN for super_admins.
 *
 * Viser nederst til højre hvem der netop er gået ind i appen: avatar og navn,
 * med en slide-up. Notifikationen forsvinder af sig selv efter 60 sekunder,
 * eller når man klikker på krydset.
 *
 * HVORNÅR UDLØSES DEN (Charlies valg 19-08-2026): både ved et nyt login OG når
 * en bruger med gyldig session bruger appen igen efter mindst 30 minutters
 * pause. Kun login ville være for stille: med SSO og lange sessioner logger
 * folk sjældent ind, målt til omkring 1 gang om dagen, og så ville en bureau-
 * bruger der arbejder i appen hele eftermiddagen aldrig blive vist.
 *
 * ⚠ INGEN CATCH-UP (også Charlies valg): der vises kun hvad der sker mens man
 * selv har appen fremme. Derfor er den FØRSTE hentning en NULSTILLING, ikke en
 * hændelse. Uden det ville enhver genindlæsning af siden give en notifikation
 * for hver bruger der tilfældigvis var inde i forvejen.
 *
 * ⚠ SERVEREN AFGØR IKKE HVAD DER ER NYT. Den svarer kun på hvem der er aktiv
 * nu, og denne komponent husker selv hvad den har vist. Havde serveren ført
 * "vist"-tilstanden, ville 2 åbne faner slås om den, og en notifikation kunne
 * forsvinde for det ene vindue fordi det andet nåede at hente den først.
 *
 * FLERE BRUGERE I SAMME RUNDE DELER ÉN NOTIFIKATION (Charlie samme dag): logger
 * 2 ind inden for samme interval, står de sammen med hver sin avatar, frem for
 * at stable 2 kasser oven på hinanden.
 */

import { computed, onUnmounted, ref, watch } from 'vue';

import { auth } from '../composables/useAuth';

/** Samme kadence som serverens debouncede last_seen-skrivning (30 sekunder). */
const POLL_MS = 30_000;
/**
 * Hvor lang en pause skal være, før en tilbagevenden tæller som en ny tilgang.
 * 30 minutter er valgt så et frokostophold eller et møde giver en notifikation,
 * mens en tur ud i et andet program ikke gør.
 */
const RETURN_AFTER_MS = 30 * 60 * 1000;
/** Notifikationen lukker sig selv efter 60 sekunder (Charlies krav). */
const AUTO_DISMISS_MS = 60_000;
/** Så mange avatarer og navne vises; derover skæres der af med "+N". */
const MAX_AVATARS = 3;

interface ActiveUser {
  userId: number;
  email: string;
  displayName: string | null;
  avatarUrl: string | null;
  lastSeenAt: number;
  sessionStartedAt: number;
}
interface ToastPerson {
  userId: number;
  name: string;
  initials: string;
  avatarUrl: string | null;
  /** true = logget ind netop nu, false = tilbage efter en pause. */
  isNewLogin: boolean;
}
interface Toast {
  id: number;
  people: ToastPerson[];
}

const toasts = ref<Toast[]>([]);
/** Sidst kendte aktivitet pr. bruger — grundlaget for pause-målingen. */
const lastSeenByUser = new Map<number, number>();
/** Første hentning nulstiller kun; den må aldrig vise noget. */
let baselineDone = false;
let nextToastId = 1;
let pollTimer: number | undefined;
const dismissTimers = new Map<number, number>();

const isSuperAdmin = computed(() => (auth.user?.roles ?? []).includes('super_admin'));

/** Samme navne- og initial-regler som brugerfeltet i sidebaren. */
function nameOf(u: ActiveUser): string {
  return u.displayName?.trim() || u.email.split('@')[0] || u.email;
}
function initialsOf(u: ActiveUser): string {
  const base = (u.displayName?.trim() || u.email).split('(')[0]!.trim();
  const words = base.split(/\s+/).filter((w) => /^\p{L}/u.test(w));
  const ini = words.slice(0, 2).map((w) => w[0] ?? '').join('') || u.email[0] || '?';
  return ini.toUpperCase();
}

function dismiss(id: number): void {
  toasts.value = toasts.value.filter((t) => t.id !== id);
  const timer = dismissTimers.get(id);
  if (timer !== undefined) {
    window.clearTimeout(timer);
    dismissTimers.delete(id);
  }
}

function push(people: ToastPerson[]): void {
  const id = nextToastId++;
  toasts.value = [...toasts.value, { id, people }];
  dismissTimers.set(id, window.setTimeout(() => dismiss(id), AUTO_DISMISS_MS));
}

async function poll(): Promise<void> {
  if (!isSuperAdmin.value) return;
  let data: { users?: ActiveUser[] };
  try {
    const res = await fetch('/api/settings/active-users', { credentials: 'same-origin' });
    if (!res.ok) return; // 403 v. rolle-skift, 5xx v. drift: prøv igen næste runde
    data = (await res.json()) as { users?: ActiveUser[] };
  } catch {
    return; // netværks-udfald må ikke vælte pollingen
  }

  const fresh: ToastPerson[] = [];
  for (const u of data.users ?? []) {
    // Man skal ikke have besked om sig selv.
    if (u.userId === auth.user?.id) continue;
    const previous = lastSeenByUser.get(u.userId);
    lastSeenByUser.set(u.userId, u.lastSeenAt);
    if (!baselineDone) continue;
    const erNy = previous === undefined;
    const erTilbage = previous !== undefined && u.lastSeenAt - previous >= RETURN_AFTER_MS;
    if (!erNy && !erTilbage) continue;
    fresh.push({
      userId: u.userId,
      name: nameOf(u),
      initials: initialsOf(u),
      avatarUrl: u.avatarUrl,
      // Er sessionen selv frisk, ER det et login; ellers er det en tilbagevenden.
      isNewLogin: u.lastSeenAt - u.sessionStartedAt < RETURN_AFTER_MS,
    });
  }
  baselineDone = true;
  if (fresh.length > 0) push(fresh);
}

/** Overskriften: "er logget ind" kun når ALLE i notifikationen er nye logins. */
function headingOf(t: Toast): string {
  const alleNye = t.people.every((p) => p.isNewLogin);
  if (t.people.length === 1) return alleNye ? 'Logget ind' : 'Tilbage i appen';
  return alleNye ? 'Logget ind' : 'I appen';
}

/**
 * Navnene som dansk opremsning: "Charlie", "Charlie og Dennis", "Charlie,
 * Dennis og Nairi". Er der flere end 3, skæres der af med et antal frem for at
 * lade notifikationen vokse, og hele listen står i title-attributten.
 */
function namesOf(t: Toast): string {
  const navne = t.people.map((p) => p.name);
  if (navne.length > MAX_AVATARS) {
    const rest = navne.length - MAX_AVATARS;
    return `${navne.slice(0, MAX_AVATARS).join(', ')} og ${rest} ${rest === 1 ? 'anden' : 'andre'}`;
  }
  if (navne.length === 1) return navne[0]!;
  return `${navne.slice(0, -1).join(', ')} og ${navne[navne.length - 1]}`;
}

function stopPolling(): void {
  if (pollTimer !== undefined) window.clearInterval(pollTimer);
  pollTimer = undefined;
}

/**
 * ⚠ Pollingen startes af en WATCHER, ikke af onMounted. Komponenten monteres
 * FØR `/api/auth/me` er hentet, så `isSuperAdmin` er falsk på mount-tidspunktet;
 * et tjek dér ville slå notifikationen fra permanent for alle, også
 * super_admins. Watcheren fanger rollen når brugeren lander, og lukker
 * tilsvarende ned igen ved log ud, så en efterfølgende bruger ved samme skærm
 * ikke arver pollingen.
 */
watch(
  isSuperAdmin,
  (erSuperAdmin) => {
    if (erSuperAdmin) {
      if (pollTimer !== undefined) return; // allerede i gang
      void poll();
      pollTimer = window.setInterval(() => void poll(), POLL_MS);
    } else {
      stopPolling();
      // Ny bruger ved skærmen skal have sin egen baseline og sit eget tomme bord.
      baselineDone = false;
      lastSeenByUser.clear();
      toasts.value = [];
    }
  },
  { immediate: true },
);

onUnmounted(() => {
  stopPolling();
  for (const timer of dismissTimers.values()) window.clearTimeout(timer);
  dismissTimers.clear();
});
</script>

<template>
  <Teleport to="body">
    <!-- Nederst til HØJRE: den globale gem-indikator ligger centreret i bunden
         (SavingIndicator), så de 2 kan være fremme samtidig uden at overlappe.
         Samme z-[200] og kort-stil som den, jf. UI-konsistens-reglen. -->
    <div
      class="fixed bottom-[calc(var(--mobile-nav-h)+1.25rem)] right-5 z-[200] flex w-[min(92vw,20rem)] flex-col gap-2 print:hidden"
      role="status"
      aria-live="polite"
    >
      <TransitionGroup name="toast-up">
        <div
          v-for="t in toasts"
          :key="t.id"
          class="card-surface overflow-hidden rounded-xl border border-line shadow-lg"
        >
          <div class="flex items-start gap-3 px-3.5 py-3">
            <!-- Avatarer: Graph-billedet, ellers initialerne.
                 ⚠ De står SIDE OM SIDE, ikke som en overlappende stak: med
                 overlap forsvandt initial-avataren halvt ind under den næste,
                 og initialer er i forvejen svagere end et billede. Over 3
                 personer bliver til "+N", så notifikationen ikke vokser. -->
            <div class="flex shrink-0 items-center gap-1">
              <template v-for="p in t.people.slice(0, MAX_AVATARS)" :key="p.userId">
                <img
                  v-if="p.avatarUrl"
                  :src="p.avatarUrl"
                  :alt="p.name"
                  class="h-7 w-7 rounded-full object-cover ring-1 ring-line"
                />
                <span
                  v-else
                  class="grid h-7 w-7 place-items-center rounded-full bg-subtle text-[10px] font-semibold text-ink ring-1 ring-line select-none"
                  aria-hidden="true"
                >{{ p.initials }}</span>
              </template>
              <span
                v-if="t.people.length > MAX_AVATARS"
                class="grid h-7 w-7 place-items-center rounded-full bg-subtle text-[10px] font-semibold text-muted ring-1 ring-line select-none"
                aria-hidden="true"
              >+{{ t.people.length - MAX_AVATARS }}</span>
            </div>

            <div class="min-w-0 flex-1 leading-tight">
              <p class="text-[11px] font-medium uppercase tracking-wide text-faint">
                {{ headingOf(t) }}
              </p>
              <p class="mt-0.5 text-[13px] text-ink" :title="t.people.map((p) => p.name).join(', ')">
                {{ namesOf(t) }}
              </p>
            </div>

            <button
              type="button"
              class="-mr-1 -mt-1 shrink-0 rounded-lg p-1 text-faint transition-colors hover:bg-hover hover:text-ink"
              aria-label="Luk"
              @click="dismiss(t.id)"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                aria-hidden="true"
              >
                <path d="M4 4l8 8M12 4l-8 8" />
              </svg>
            </button>
          </div>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<style scoped>
/* Samme slide-up-timing som den globale gem-indikator. */
.toast-up-enter-active,
.toast-up-leave-active {
  transition:
    transform 0.28s ease,
    opacity 0.28s ease;
}
.toast-up-enter-from,
.toast-up-leave-to {
  transform: translateY(1.5rem);
  opacity: 0;
}
/* Når en notifikation over de andre lukkes, glider resten pænt på plads. */
.toast-up-move {
  transition: transform 0.28s ease;
}
</style>
